/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ResultsIndexPage — /results
 *
 * Every event's motion board in one place. Public, like the boards themselves:
 * somebody who ran a race and wants to see where they came should not have to
 * sign in, and a spectator never will.
 *
 * WHY THIS EXISTS. Until now a board could only be reached by typing
 * /results/<id>. This is the way in.
 *
 * Live first, then what is coming, then what has been — and inside each band
 * the nearest in time first, so what somebody is most likely looking for sits
 * nearest the top.
 *
 * ONE READ. Earlier cuts used the public table endpoint — which caps a page at
 * ten rows, ignores page_size and refuses to paginate, so the index silently
 * showed the first ten events and nothing else. A saved query answers every
 * event with its activities in one request. See boardIndexApi's header.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import boardClient from './boardClient';
import { fetchEventActivities } from './boardIndexApi';
import { groupForIndex, searchEvents, datesOf } from './boardIndexModel';
// The search box the other public list pages use — debounced, with a clear
// button that actually restores the full list.
import PublicSearchBar from '../../../components/public/PublicSearchBar';

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
// HAIR was the old card's 1px border. The cards now sit on a soft shadow,
// like the other public lists, so there is no hairline to draw.
// const HAIR = 'rgba(36, 23, 8, .10)';

const BANDS = [
  { key: 'live', label: 'Happening now' },
  { key: 'upcoming', label: 'Coming up' },
  { key: 'past', label: 'Finished' },
];

/**
 * Three lines of the blurb and no more.
 *
 * `-webkit-line-clamp` rather than cutting the string: it breaks on whatever
 * line actually fits the card at the width it ends up with, so a narrow phone
 * card and a wide desktop one both end cleanly. Cutting at N characters is
 * guesswork about a width nobody knows at the time. The full text stays in
 * `title`, so hovering still gives it.
 */
const clamp = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  // A title or blurb with no spaces in it — a slug, a URL, a line of test
  // junk — has nowhere to break, so the clamp cannot catch it and the text
  // runs straight out of the card. This breaks it wherever it has to.
  overflowWrap: 'anywhere',
});

/** The picture, when the event has one; its initial, when it does not. */
function CoverImage({ event }) {
  const [broken, setBroken] = useState(false);
  const url = event.coverImageUrl;

  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        onError={() => setBroken(true)}
        style={{
          display: 'block', width: '100%', height: 168, objectFit: 'cover',
          background: '#F1E9DA',
        }}
      />
    );
  }

  // Not every event has a picture, and a grid with a hole in it reads as
  // broken. A lettered panel in the board's own ground keeps the row even.
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100%', height: 168,
        background: 'linear-gradient(100deg, #2A1408 0%, #22120A 42%, #150C06 100%)',
        color: 'rgba(250,243,231,.5)', fontSize: 58, fontWeight: 900, letterSpacing: '-.04em',
      }}
    >
      {String(event.title || '?').trim().charAt(0).toUpperCase()}
    </div>
  );
}

function EventCard({ event }) {
  const dates = datesOf(event);
  const published = event.races.filter((race) => race.resultsPublished).length;
  // The card opens the event's FIRST board. Listing every activity as its own
  // button worked for a 5K-and-10K event and fell apart on one with five
  // activities — the buttons became the card. The board it lands on already
  // carries an activity switcher, so choosing between them belongs there,
  // where there is room for it, not here.
  const first = event.races[0];
  const count = event.races.length || event.raceCount || 0;

  const body = (
    <>
      <div style={{ position: 'relative' }}>
        <CoverImage event={event} />
        {published > 0 && (
          <span
            style={{
              position: 'absolute', left: 12, top: 12, padding: '5px 11px', borderRadius: 999,
              background: ACCENT, color: '#fff', fontSize: 10, fontWeight: 800,
              letterSpacing: '.14em', textTransform: 'uppercase',
            }}
          >
            Results out
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '16px 18px 18px' }}>
        <h3
          title={event.title}
          style={{ ...clamp(2), margin: 0, fontSize: 17, fontWeight: 700, color: INK, lineHeight: 1.35 }}
        >
          {event.title}
        </h3>

        {(dates || event.location) && (
          <p style={{ ...clamp(1), margin: '6px 0 0', fontSize: 13, color: MUTED }}>
            {[dates, event.location].filter(Boolean).join(' · ')}
          </p>
        )}

        {event.description && (
          <p
            title={event.description}
            style={{ ...clamp(3), margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.55, color: '#3B2A14' }}
          >
            {event.description}
          </p>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginTop: 'auto', paddingTop: 16,
        }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: MUTED }}>
            {count} {count === 1 ? 'race' : 'races'}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 800,
            letterSpacing: '.1em', textTransform: 'uppercase', color: ACCENT,
          }}
          >
            View results
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </>
  );

  // An event whose races have not come back yet cannot be opened, so it is a
  // card rather than a dead link.
  if (!first) {
    return <article className="ri-card">{body}</article>;
  }
  return (
    <Link
      to={`/results/${first.id}`}
      className="ri-card"
      aria-label={`${event.title} — results`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      {body}
    </Link>
  );
}

/** Events per page, per band. */
const PAGE_SIZE = 6;

function Pager({ page, pages, onPage, label }) {
  if (pages <= 1) return null;
  const btn = (on) => ({
    minHeight: 40, padding: '0 16px', borderRadius: 999, cursor: on ? 'pointer' : 'default',
    border: `1.5px solid ${on ? ACCENT : '#E4DCCF'}`, background: '#fff',
    color: on ? ACCENT : '#C9BEAD', fontSize: 13, fontWeight: 700,
  });
  return (
    <nav aria-label={`${label} pages`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 18 }}>
      <button type="button" style={btn(page > 1)} disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ← Previous
      </button>
      <span style={{ fontSize: 13, fontWeight: 700, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
        {page} / {pages}
      </span>
      <button type="button" style={btn(page < pages)} disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next →
      </button>
    </nav>
  );
}

export default function ResultsIndexPage() {
  const [term, setTerm] = useState('');
  // One page number per band, reset whenever the search changes — otherwise a
  // search that returns two events leaves you on page 3 of nothing.
  const [pages, setPages] = useState({});

  const board = useQuery({
    queryKey: ['board-index', 'event-activities'],
    queryFn: ({ signal }) => fetchEventActivities(boardClient, { signal }),
    staleTime: 120000,
  });

  const grouped = useMemo(
    () => groupForIndex(board.data?.events || [], board.data?.races || []),
    [board.data],
  );

  const bands = BANDS
    .map((band) => ({ ...band, events: searchEvents(grouped[band.key], term) }))
    .filter((band) => band.events.length > 0);

  const loading = board.isLoading;
  const failed = board.isError;

  const onSearch = useCallback((q) => { setTerm(q); setPages({}); }, []);

  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 72px' }}>
      <style>{`
        .ri-card {
          display: flex; flex-direction: column; height: 100%;
          background: #fff; border-radius: 16px; overflow: hidden; border: 0;
          box-shadow: 0 2px 8px rgba(0,0,0,.06);
          transition: box-shadow .2s ease, transform .2s ease;
        }
        .ri-card:hover { box-shadow: 0 8px 16px rgba(0,0,0,.12); transform: translateY(-4px); }
        .ri-card:active { transform: translateY(0) scale(.994); transition-duration: .1s; }
        .ri-card:focus-visible { outline: 3px solid ${ACCENT}; outline-offset: 3px; }
        @media (prefers-reduced-motion: reduce) {
          .ri-card, .ri-card:hover { transition: none; transform: none }
        }
      `}</style>

      <header style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: INK, letterSpacing: '-.01em' }}>
          Motion board
        </h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
          Every event&rsquo;s results, as they come in.
        </p>
      </header>

      {/* The same search box the other public lists use: it debounces as you
          type, so there is no Search button to press, and its own clear button
          puts every result back. The hand-rolled one here only applied the term
          on submit, so clearing the field left the old results on screen. */}
      <div style={{ marginBottom: 22 }}>
        <PublicSearchBar onSearch={onSearch} onClear={() => onSearch('')} placeholder="Search an event or a race" />
      </div>

      {loading && <p style={{ color: MUTED, fontSize: 14 }}>Loading the boards&hellip;</p>}

      {failed && !loading && (
        <p role="alert" style={{ color: '#8A2C06', fontSize: 14 }}>
          The boards could not be loaded just now. Try again in a moment.
        </p>
      )}

      {!loading && !failed && bands.length === 0 && (
        <p style={{ color: MUTED, fontSize: 14 }}>
          {term ? `Nothing matches “${term}”.` : 'No events have a board yet.'}
        </p>
      )}

      {bands.map((band) => {
        const page = pages[band.key] || 1;
        const total = Math.max(1, Math.ceil(band.events.length / PAGE_SIZE));
        const safe = Math.min(page, total);
        const visible = band.events.slice((safe - 1) * PAGE_SIZE, safe * PAGE_SIZE);
        return (
          <section key={band.key} aria-label={band.label} style={{ marginBottom: 34 }}>
            <h2
              style={{
                margin: '0 0 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.12em',
                textTransform: 'uppercase', color: MUTED,
              }}
            >
              {band.label}
              <span style={{ marginLeft: 8, color: '#A4977F', fontWeight: 600 }}>
                {band.events.length}
              </span>
            </h2>
            {/* auto-FILL, not auto-fit. With auto-fit the empty tracks collapse,
                so a search matching one event stretched that card across the
                whole row and it read as a different component. */}
            <div
              style={{
                display: 'grid', gap: 16,
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              }}
            >
              {visible.map((event) => <EventCard key={event.id} event={event} />)}
            </div>
            <Pager
              page={safe}
              pages={total}
              label={band.label}
              onPage={(n) => setPages((prev) => ({ ...prev, [band.key]: n }))}
            />
          </section>
        );
      })}
    </main>
  );
}
